import GradingIcon from '@mui/icons-material/Grading';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { LoadingButton } from 'probonio-shared-ui/component/button';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewDialogState } from '../../../component/dialog';
import { SearchInput } from '../../../component/form/SearchInput';
import { HorizontalBox } from '../../../component/layout/BoxLayout';
import { CostCenterSelector } from '../../selectors/costCenters/CostCenterSelector';
import { DepartmentSelector } from '../../selectors/departments/DepartmentSelector';
import { WorkLocationsSelector } from '../../selectors/workLocations/workLocationsSelector';
import { BenefitFilter, BenefitSelector } from '../../userManagement/BenefitSelector';
import { EmployeeClearFilterButton } from './EmployeeClearFilterButton';
import { EmployeeExportDialog } from './EmployeeExportDialog';
import { EmployeeFilter, EmployeeFilterState } from './employeeFilterState';
import { useEmployeeListFilterActive } from './useEmployeeListFilterActive';

interface Props {
  filterState: EmployeeFilterState;
  showBenefitFilter?: boolean;
  showStatusFilter?: boolean;
  showDepartmentFilter?: boolean;
  showCostCenterFilter?: boolean;
  showWorkLocationsFilter?: boolean;
  showHiddenFilter?: boolean;
  showExport?: boolean;
}

export const EmployeeFilterSelector: React.FC<Props> = ({
  filterState,
  showBenefitFilter,
  showStatusFilter,
  showDepartmentFilter = true,
  showCostCenterFilter = true,
  showWorkLocationsFilter = true,
  showHiddenFilter,
  showExport,
}) => {
  const { t } = useTranslation('usersModule');
  const exportDialog = useNewDialogState();
  const [filterActive, setFilterActive] = useEmployeeListFilterActive();

  const handleFilterChange = useCallback(
    (key: keyof EmployeeFilter, value?: string) => {
      const setFilter = filterState.setFilter;
      setFilter(prev => ({
        ...prev,
        [key]: value,
      }));
    },
    [filterState.setFilter],
  );

  const handleChangeSearchText = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    event => {
      handleFilterChange('searchText', event.target.value);
    },
    [handleFilterChange],
  );

  const handleDepartmentChange = useCallback(
    (department?: string) => {
      handleFilterChange('department', department);
    },
    [handleFilterChange],
  );

  const handleCostCenterChange = useCallback(
    (costCenter?: string) => {
      handleFilterChange('costCenter', costCenter);
    },
    [handleFilterChange],
  );
  const handleWorkLocationsChange = useCallback(
    (workLocations?: string) => {
      handleFilterChange('workLocations', workLocations);
    },
    [handleFilterChange],
  );

  const handleSetBenefitFilter = useCallback(
    (benefitFilter: BenefitFilter) => {
      filterState.setFilter(filter => ({ ...filter, benefitFilter }));
    },
    [filterState],
  );

  const handleChangeStatus = useCallback(
    (event: SelectChangeEvent<string>) => {
      filterState.setFilter(filter => ({
        ...filter,
        showActiveUser: (event.target.value === 'ALL' ? undefined : event.target.value) as EmployeeFilter['showActiveUser'],
      }));
    },
    [filterState],
  );

  const handleToggleIncludeHidden = useCallback(() => {
    setFilterActive(!filterActive);
  }, [filterActive, setFilterActive]);

  const handleRevertFilters = useCallback(() => {
    const setFilter = filterState.setFilter;
    setFilter(filter => {
      const clearedFilter = { ...filter, department: undefined, costCenter: undefined, searchText: undefined, workLocations: undefined };
      if (showBenefitFilter) {
        clearedFilter.benefitFilter = undefined;
      }
      if (showStatusFilter) {
        clearedFilter.showActiveUser = undefined;
      }
      return clearedFilter;
    });
  }, [filterState.setFilter, showBenefitFilter, showStatusFilter]);

  const isFilterSet =
    (showBenefitFilter && filterState.filter.benefitFilter) ||
    (showDepartmentFilter && filterState.filter.department) ||
    (showCostCenterFilter && filterState.filter.costCenter) ||
    (showWorkLocationsFilter && filterState.filter.workLocations) ||
    filterState.filter.searchText ||
    (showStatusFilter && filterState.filter.showActiveUser);

  return (
    <HorizontalBox sx={{ gap: 1, flexGrow: 1, marginBottom: 1.5 }}>
      {showHiddenFilter && (
        <Tooltip title={t(filterActive ? 'usersList.includeHidden' : 'usersList.excludeHidden')}>
          <IconButton
            size="small"
            color={filterActive ? 'default' : 'primary'}
            onClick={handleToggleIncludeHidden}
            data-test-id="toggle-include-hidden"
          >
            {filterActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
      )}
      <SearchInput
        data-test-id="search-bar"
        value={filterState.filter.searchText || ''}
        onChange={handleChangeSearchText}
        size="small"
        sx={{ width: 250 }}
      />

      {showDepartmentFilter && (
        <DepartmentSelector value={filterState.filter.department} onChange={handleDepartmentChange} size="small" sx={{ width: 250 }} />
      )}
      {showCostCenterFilter && (
        <CostCenterSelector value={filterState.filter.costCenter} onChange={handleCostCenterChange} size="small" sx={{ width: 250 }} />
      )}
      {showWorkLocationsFilter && (
        <WorkLocationsSelector
          value={filterState.filter.workLocations}
          onChange={handleWorkLocationsChange}
          size="small"
          sx={{ width: 250 }}
        />
      )}
      {showStatusFilter && (
        <FormControl size="small">
          <InputLabel htmlFor="status-selector">{t('usersList.statusFilterLabel')}</InputLabel>
          <Select
            data-test-id="status-selector"
            label={t('usersList.statusFilterLabel')}
            id="status-selector"
            onChange={handleChangeStatus}
            value={filterState.filter.showActiveUser || 'ALL'}
            sx={{ width: 250, height: '100%', backgroundColor: 'background.paper' }}
          >
            <MenuItem value="ALL">
              <em>{t('departmentModule:all')}</em>
            </MenuItem>
            <MenuItem value="ACTIVE">{t('usersPage:tab.active')}</MenuItem>
            <MenuItem value="REGISTRATION">{t('usersPage:tab.registration')}</MenuItem>
          </Select>
        </FormControl>
      )}
      {showBenefitFilter && (
        <BenefitSelector benefitFilter={filterState.filter.benefitFilter} onChangeBenefitFilter={handleSetBenefitFilter} />
      )}

      {isFilterSet && <EmployeeClearFilterButton onRevertFilters={handleRevertFilters} />}

      {showExport && (
        <>
          <Tooltip title={t('usersList.exportDialog.title')}>
            <LoadingButton sx={{ marginLeft: 'auto', p: 1 }} onClick={exportDialog.handleOpen} variant="topBarButtonBorder" size="small">
              <GradingIcon />
            </LoadingButton>
          </Tooltip>
          <EmployeeExportDialog dialogState={exportDialog.dialogState} />
        </>
      )}
    </HorizontalBox>
  );
};
